/**
 * Dedicated search hook with debounce, abort, and recent searches.
 *
 * Calls GET /api/v1/boxes?q=... and transforms results into
 * item-centric SearchResult objects grouped by match type.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { searchBoxes } from '../api/client';
import type { BoxWithItems, Item } from '@shared/types';

// ── Types ─────────────────────────────────────────────────────

export type MatchType = 'item' | 'box' | 'location';

export interface SearchResult {
  box: BoxWithItems;
  matchedItems: Item[];
  matchType: MatchType;
}

export interface UseSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  maxRecentSearches?: number;
}

export interface UseSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  recentSearches: string[];
  clearRecentSearches: () => void;
  removeRecentSearch: (q: string) => void;
}

// ── Local storage ─────────────────────────────────────────────

const STORAGE_KEY = 'peekbox:recent-searches';

function loadRecentSearches(max: number): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s: unknown) => typeof s === 'string').slice(0, max);
  } catch {
    return [];
  }
}

function saveRecentSearches(searches: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
  } catch {
    // localStorage may be unavailable
  }
}

// ── Match classification ──────────────────────────────────────

function classifyResults(boxes: BoxWithItems[], query: string): SearchResult[] {
  const q = query.toLowerCase();

  return boxes.map((box) => {
    const matchedItems = box.items.filter((item) => item.name.toLowerCase().includes(q));

    const boxNameMatches = box.name.toLowerCase().includes(q);
    const locationMatches = !!box.location && box.location.toLowerCase().includes(q);

    // Priority: item > box > location
    let matchType: MatchType = 'box';
    if (matchedItems.length > 0) {
      matchType = 'item';
    } else if (locationMatches && !boxNameMatches) {
      matchType = 'location';
    }

    return { box, matchedItems, matchType };
  });
}

// ── Hook ──────────────────────────────────────────────────────

export function useSearch(options: UseSearchOptions = {}): UseSearchReturn {
  const { debounceMs = 300, minQueryLength = 2, maxRecentSearches = 5 } = options;

  const [query, setQueryState] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>(() =>
    loadRecentSearches(maxRecentSearches),
  );

  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  // Save a successful query to recent searches
  const addRecentSearch = useCallback(
    (q: string) => {
      setRecentSearches((prev) => {
        const trimmed = q.trim();
        if (!trimmed) return prev;
        const filtered = prev.filter((s) => s.toLowerCase() !== trimmed.toLowerCase());
        const updated = [trimmed, ...filtered].slice(0, maxRecentSearches);
        saveRecentSearches(updated);
        return updated;
      });
    },
    [maxRecentSearches],
  );

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    saveRecentSearches([]);
  }, []);

  const removeRecentSearch = useCallback((q: string) => {
    setRecentSearches((prev) => {
      const updated = prev.filter((s) => s !== q);
      saveRecentSearches(updated);
      return updated;
    });
  }, []);

  // Debounced search execution
  useEffect(() => {
    const trimmed = query.trim();

    // Clear results if query too short
    if (trimmed.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      // Cancel previous request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const result = await searchBoxes(trimmed);

        // Check if aborted
        if (controller.signal.aborted) return;

        const classified = classifyResults(result.data, trimmed);
        setResults(classified);
        setLoading(false);

        // Save to recent searches
        if (classified.length > 0) {
          addRecentSearch(trimmed);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Search failed');
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [query, debounceMs, minQueryLength, addRecentSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
  }, []);

  return {
    query,
    setQuery,
    results,
    loading,
    error,
    recentSearches,
    clearRecentSearches,
    removeRecentSearch,
  };
}
